import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Download, ExternalLink, Copy, Check, FileText, Music, Image, Video,
    File, Maximize2, Minimize2, Loader2, AlertCircle, RefreshCw, Sparkles
} from 'lucide-react';
import vaultService from '../../../services/vaultService';
import { smartCopy } from '../../../utils/textFormatting';

/**
 * AssetPreviewModal Component
 * 
 * A sophisticated modal for previewing vault assets including images, text files, audio, and video.
 * Features:
 * - Centered modal with semi-transparent backdrop
 * - Image preview with zoom functionality
 * - Text content display with syntax highlighting
 * - Audio/video playback
 * - Copy content to clipboard
 * - Download functionality
 */
const AssetPreviewModal = ({ isOpen, onClose, asset, onInsertToEditor }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [content, setContent] = useState(null);
    const [copied, setCopied] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Fetch content when modal opens
    useEffect(() => {
        if (isOpen && asset) {
            loadContent();
        }
        return () => {
            setContent(null);
            setIsLoading(true);
            setError(null);
        };
    }, [isOpen, asset?.id]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    /**
     * Load asset content based on type
     */
    const loadContent = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // For text files, try to fetch the actual content
            if (asset.assetType === 'text' || asset.assetType === 'document') {
                if (asset.downloadUrl) {
                    // Try fetching, but handle CORS errors gracefully
                    try {
                        const response = await fetch(asset.downloadUrl);
                        if (response.ok) {
                            const text = await response.text();
                            setContent(text);
                        } else {
                            throw new Error('Failed to load content');
                        }
                    } catch (fetchError) {
                        // CORS or network error - show what we have
                        console.warn('Could not fetch content directly (CORS), using available data:', fetchError.message);
                        setContent(asset.content || asset.contentPreview || 'Content preview not available. Use the download link to access the full content.');
                    }
                } else if (asset.content) {
                    setContent(asset.content);
                } else if (asset.contentPreview) {
                    setContent(asset.contentPreview);
                } else {
                    setContent('No content available');
                }
            }
            // For images, audio, video - the URL is enough
            else {
                setContent(asset.downloadUrl);
            }
        } catch (err) {
            console.error('Failed to load asset content:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Copy content to clipboard
     */
    const handleCopy = async () => {
        try {
            let textToCopy = '';
            if (asset.assetType === 'text' || asset.assetType === 'document') {
                textToCopy = content || asset.contentPreview || '';
            } else {
                textToCopy = asset.downloadUrl || '';
            }
            // Use smartCopy for formatting awareness (platform agnostic fallback)
            await smartCopy(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    /**
     * Handle file download - fetches file as blob and triggers actual download
     */
    const handleDownload = async () => {
        if (!asset.downloadUrl || isDownloading) return;

        setIsDownloading(true);

        try {
            // Fetch the file as a blob
            const response = await fetch(asset.downloadUrl);
            if (!response.ok) throw new Error('Failed to download file');

            const blob = await response.blob();

            // Create a temporary object URL for the blob
            const blobUrl = URL.createObjectURL(blob);

            // Create a temporary anchor element to trigger the download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = asset.fileName || asset.title || 'download';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            // Clean up after a short delay to ensure download starts
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            }, 100);
        } catch (err) {
            console.error('Download failed:', err);
            // Fallback: try opening in new tab
            window.open(asset.downloadUrl, '_blank');
        } finally {
            setIsDownloading(false);
        }
    };

    /**
     * Handle insert to editor
     */
    const handleInsert = () => {
        if (onInsertToEditor) {
            if (asset.assetType === 'text' || asset.assetType === 'document') {
                onInsertToEditor(content || asset.contentPreview || '');
            } else if (asset.assetType === 'image') {
                // Insert markdown image
                const markdown = `![${asset.fileName || asset.title}](${asset.downloadUrl})`;
                onInsertToEditor(markdown);
            } else {
                // Insert link for other types
                const markdown = `[${asset.fileName || asset.title}](${asset.downloadUrl})`;
                onInsertToEditor(markdown);
            }
        }
        onClose();
    };

    /**
     * Get icon for asset type
     */
    const getAssetIcon = () => {
        const iconProps = { size: 22 };
        switch (asset?.assetType) {
            case 'audio':
                return <Music {...iconProps} className="text-purple-500" />;
            case 'video':
                return <Video {...iconProps} className="text-rose-500" />;
            case 'image':
                return <Image {...iconProps} className="text-emerald-500" />;
            case 'document':
                return <FileText {...iconProps} className="text-blue-500" />;
            case 'text':
                return <FileText {...iconProps} className="text-slate-500" />;
            default:
                return <File {...iconProps} className="text-slate-400" />;
        }
    };

    /**
     * Get gradient colors based on asset type
     */
    const getAssetGradient = () => {
        switch (asset?.assetType) {
            case 'audio':
                return 'from-purple-500 to-indigo-600';
            case 'video':
                return 'from-rose-500 to-orange-600';
            case 'image':
                return 'from-emerald-500 to-teal-600';
            case 'document':
                return 'from-blue-500 to-cyan-600';
            case 'text':
                return 'from-slate-500 to-slate-600';
            default:
                return 'from-slate-400 to-slate-500';
        }
    };

    /**
     * Render preview content based on asset type
     */
    const renderPreview = () => {
        if (isLoading) {
            return (
                <div className="flex flex-col items-center justify-center h-72 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl">
                    <div className="relative">
                        <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-xl animate-pulse" />
                        <Loader2 size={40} className="text-indigo-500 animate-spin relative" />
                    </div>
                    <p className="mt-4 text-sm text-slate-500 font-medium">Loading preview...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-72 bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl">
                    <div className="p-4 bg-white rounded-2xl shadow-lg">
                        <AlertCircle size={32} className="text-red-400" />
                    </div>
                    <p className="mt-4 text-sm text-red-600 font-medium">{error}</p>
                    <button
                        onClick={loadContent}
                        className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-lg shadow-indigo-500/25"
                    >
                        <RefreshCw size={14} />
                        Retry
                    </button>
                </div>
            );
        }

        switch (asset?.assetType) {
            case 'image':
                return (
                    <div className="relative group">
                        <div className="flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl overflow-hidden">
                            <img
                                src={content}
                                alt={asset.fileName || asset.title}
                                className={`max-w-full object-contain transition-all duration-300 ${isFullscreen ? 'max-h-[70vh]' : 'max-h-[50vh]'}`}
                            />
                        </div>
                        <button
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="absolute top-3 right-3 p-2.5 bg-white/90 hover:bg-white rounded-xl shadow-lg backdrop-blur-sm transition-all hover:scale-105"
                        >
                            {isFullscreen ? <Minimize2 size={16} className="text-slate-600" /> : <Maximize2 size={16} className="text-slate-600" />}
                        </button>
                    </div>
                );

            case 'audio':
                return (
                    <div className="flex flex-col items-center justify-center py-12 bg-gradient-to-br from-purple-50 via-indigo-50 to-violet-100 rounded-2xl">
                        <div className="relative mb-6">
                            <div className="absolute inset-0 bg-purple-400/30 rounded-full blur-2xl animate-pulse" />
                            <div className="relative p-6 bg-white rounded-2xl shadow-xl">
                                <Music size={48} className="text-purple-500" />
                            </div>
                        </div>
                        <audio
                            controls
                            src={content}
                            className="w-full max-w-md rounded-xl"
                        >
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                );

            case 'video':
                return (
                    <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                        <video
                            controls
                            src={content}
                            className="w-full max-h-[50vh]"
                        >
                            Your browser does not support the video element.
                        </video>
                    </div>
                );

            case 'text':
            case 'document':
                return (
                    <div className="relative group mt-2">
                        {/* Decorative background gradients (Pixar style) */}
                        <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-[2.5rem] blur-xl opacity-60 transition-opacity duration-500 group-hover:opacity-100" />

                        <div className="relative bg-white/95 backdrop-blur-2xl border-2 border-slate-100/50 rounded-[2rem] p-8 max-h-[65vh] overflow-y-auto shadow-xl shadow-indigo-100/50 custom-scrollbar">
                            <div className="prose prose-slate max-w-none">
                                {(content || asset.content || asset.contentPreview) ? (
                                    <div className="text-[16px] text-slate-700 whitespace-pre-wrap font-medium leading-[1.8] tracking-wide">
                                        {(content || asset.content || asset.contentPreview)}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <FileText size={48} className="text-slate-300 mb-3" />
                                        <p className="text-slate-500 italic font-medium">No content available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl">
                        <div className="p-5 bg-white rounded-2xl shadow-lg mb-4">
                            <File size={48} className="text-slate-300" />
                        </div>
                        <p className="text-sm text-slate-500 font-medium">Preview not available for this file type</p>
                    </div>
                );
        }
    };

    /**
     * Format date
     */
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        let date;
        if (timestamp && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else if (timestamp && typeof timestamp._seconds === 'number') {
            date = new Date(timestamp._seconds * 1000);
        } else if (typeof timestamp === 'string' || timestamp instanceof Date) {
            date = new Date(timestamp);
        } else if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else {
            return '';
        }
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!asset) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop - Semi-transparent with blur */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[10000]"
                    />

                    {/* Modal Container - Centered */}
                    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{
                                type: 'spring',
                                damping: 25,
                                stiffness: 300,
                                mass: 0.8
                            }}
                            className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl shadow-black/20 overflow-hidden pointer-events-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header with gradient accent */}
                            <div className="relative">
                                {/* Gradient accent bar */}
                                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getAssetGradient()}`} />

                                <div className="px-6 py-5 flex items-start justify-between">
                                    <div className="flex items-start gap-4">
                                        {/* Asset type icon with gradient background */}
                                        <div className={`p-3 rounded-2xl bg-gradient-to-br ${getAssetGradient()} shadow-lg`}>
                                            {React.cloneElement(getAssetIcon(), {
                                                className: 'text-white',
                                                size: 24
                                            })}
                                        </div>

                                        <div className="min-w-0 pt-1">
                                            <h3 className="text-xl font-bold text-slate-900 font-['Outfit'] tracking-tight truncate max-w-md">
                                                {asset.fileName || asset.title}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 uppercase tracking-wide">
                                                    {asset.assetType}
                                                </span>
                                                <span className="text-slate-300">•</span>
                                                <span className="text-sm text-slate-500 font-medium">
                                                    {vaultService.formatFileSize(asset.size)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Close button */}
                                    <button
                                        onClick={onClose}
                                        className="p-2.5 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-all hover:scale-105 active:scale-95"
                                        title="Close (Esc)"
                                    >
                                        <X size={20} strokeWidth={2} />
                                    </button>
                                </div>
                            </div>

                            {/* Preview Content */}
                            <div className="px-6 pb-4">
                                {renderPreview()}
                            </div>

                            {/* Metadata Section */}
                            <div className="px-6 py-3 bg-slate-50/80 border-y border-slate-100">
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2 text-slate-500">
                                        <span className="font-medium">Created:</span>
                                        <span>{formatDate(asset.createdAt)}</span>
                                    </div>
                                    {asset.episodeId && (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                                            <Sparkles size={12} />
                                            Linked to episode
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Actions Footer */}
                            <div className="px-6 py-4 flex items-center justify-between gap-3 bg-white">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleCopy}
                                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${copied
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check size={16} className="text-emerald-600" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={16} />
                                                Copy
                                            </>
                                        )}
                                    </button>

                                    {asset.downloadUrl && (
                                        <>
                                            <a
                                                href={asset.downloadUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 rounded-xl transition-all"
                                            >
                                                <ExternalLink size={16} />
                                                Open
                                            </a>
                                            <button
                                                type="button"
                                                onClick={handleDownload}
                                                disabled={isDownloading}
                                                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isDownloading ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Download size={16} />
                                                        Download
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {onInsertToEditor && (
                                    <button
                                        onClick={handleInsert}
                                        className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r ${getAssetGradient()}`}
                                    >
                                        <Sparkles size={16} />
                                        Insert to Editor
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AssetPreviewModal;
